// @flow

import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line max-len
import type { Row } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress/Table/Table.types';
import tabMessages from '../messages';
import { getUrlWithSelectedPropertyId } from '../../../../utils/navigation-helpers';
import {
  determineIf59aIsDisabled,
  isGrcMissingPriorFullCertData,
} from '../utils';
import type {
  CancelGrossRentCertificationModalCert,
  EditGrossRentCertificationModalCert,
  GrossRentCertification,
} from '../types';

type EditGrossRentCertificationModalOpenArgs = {
  affordableQualificationId: string,
  grossRentCert: EditGrossRentCertificationModalCert,
  modalType: string,
};

type UseRowsOptions = {
  canCancelCertifications: boolean,
  certifications: GrossRentCertification[],
  download59A: (affordableQualificationId: string) => void,
  generate59AInFlightIds: Array<string>,
  modalProps: {
    handleOpen: (args: EditGrossRentCertificationModalOpenArgs) => void,
  },
  openCancelModal: (cert: CancelGrossRentCertificationModalCert) => void,
};

const EDIT_CERT_TOOLTIP_PROPS = {
  slotProps: {
    tooltip: {
      sx: { maxWidth: 210 },
    },
  },
};

// A null value on the `number` variant falls through to the table's own right-aligned
// "---", so an inapplicable or absent amount needs no branch of its own: `available`
// only has to suppress the NC* and currency cases. It is false for two distinct causes: a
// mutually-exclusive counterpart that changed (the resident rent / utility reimbursement
// pair rule), or the cert having no prior certification data (HUD cert or manually-entered)
// to compute a value from.
const getGrossRentCurrencyCell = (
  value: number | null | void,
  available: boolean,
) => {
  const noChange = value === null && available;
  const hasValue = typeof value === 'number' && available;

  let variant = 'number';
  let finalValue = null;
  if (noChange) {
    finalValue = <span>NC*</span>;
  } else if (hasValue) {
    variant = 'currency';
    finalValue = value;
  }

  return { align: 'right', variant, value: finalValue };
};

// HUD treats resident rent and utility reimbursement as mutually exclusive: a household
// either pays a resident rent or receives a utility reimbursement, never both. When only
// one side of the pair changed, the other side doesn't apply to this cert, so it should
// read "---" rather than "NC*", which would misreport it as an inapplicable column that
// simply didn't change.
const getResidentRentUtilityReimbursementApplicable = (
  newResidentRent: number | null | void,
  newUtilityReimbursement: number | null | void,
) => {
  const residentRentHasValue = typeof newResidentRent === 'number';
  const utilityReimbursementHasValue =
    typeof newUtilityReimbursement === 'number';

  const onlyUtilityReimbursementChanged =
    newResidentRent === null && utilityReimbursementHasValue;
  const onlyResidentRentChanged =
    newUtilityReimbursement === null && residentRentHasValue;
  // Tie-break: when both changed to $0, resident rent shows the value and the
  // reimbursement is suppressed instead of displaying a redundant $0.
  const bothChangedToZero =
    newResidentRent === 0 && newUtilityReimbursement === 0;

  return {
    residentRentApplicable: !onlyUtilityReimbursementChanged,
    utilityReimbursementApplicable: !(
      onlyResidentRentChanged || bothChangedToZero
    ),
  };
};

export const useRows = ({
  canCancelCertifications,
  certifications,
  download59A,
  generate59AInFlightIds,
  modalProps: { handleOpen: handleOpenModal },
  openCancelModal,
}: UseRowsOptions) => {
  return useMemo(() => {
    return certifications.map((certification) => {
      const {
        affordableQualificationId,
        anticipatedVoucherDate,
        canCancel,
        floorPlanName,
        grEffectiveDate,
        headOfHousehold,
        newGrossRent,
        newHUDUA,
        newResidentRent,
        newSubsidy,
        newUtilityReimbursement,
        occupiedOnNTV,
        otherCertInProgress,
        prevMissingCert,
        priorCertificationDate,
        residentId,
        residentSignatureRequired,
        unit,
      } = certification;

      const missingPreviousCertification =
        isGrcMissingPriorFullCertData(certification);
      const hasPriorCertData = !missingPreviousCertification;
      const disableGenerate59A = determineIf59aIsDisabled(certification);
      const disableEditCertification = !missingPreviousCertification;
      const { residentRentApplicable, utilityReimbursementApplicable } =
        getResidentRentUtilityReimbursementApplicable(
          newResidentRent,
          newUtilityReimbursement,
        );

      const row: Row = {
        id: affordableQualificationId,
        ...(missingPreviousCertification
          ? { sx: { backgroundColor: 'error.lighter' } }
          : {}),
        action: {
          variant: 'menu',
          name: 'actionsMenu',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'editCert',
              text: <FormattedMessage {...tabMessages.editCertification} />,
              disabled: disableEditCertification,
              tooltip: disableEditCertification ? (
                <FormattedMessage
                  {...tabMessages.editCertificationDisabledTooltip}
                />
              ) : undefined,
              tooltipProps: EDIT_CERT_TOOLTIP_PROPS,
              onClick: ({ value }) =>
                handleOpenModal({
                  affordableQualificationId,
                  grossRentCert: {
                    prevMissingCert,
                    unit,
                    headOfHousehold,
                    grEffectiveDate,
                  },
                  modalType: value,
                }),
            },
            // ENG-30690. Only a certification the backend reports as cancellable gets the
            // option at all: it was started from the resident profile, is still open, and is
            // neither a reopen nor a correction. The permission is checked here as well as on
            // the endpoint, so a user without it never gets an option that would only 403.
            ...(canCancel === true && canCancelCertifications
              ? [
                  {
                    value: 'cancelCert',
                    text: (
                      <FormattedMessage {...tabMessages.cancelCertification} />
                    ),
                    onClick: () =>
                      openCancelModal({
                        affordableQualificationId,
                        unit,
                        headOfHousehold,
                      }),
                  },
                ]
              : []),
          ],
        },
        unit: {
          variant: 'string',
          value: unit,
        },
        floorPlan: {
          variant: 'string',
          value: floorPlanName,
        },
        headOfHousehold:
          headOfHousehold && residentId
            ? {
                variant: 'link',
                to: getUrlWithSelectedPropertyId(
                  occupiedOnNTV === 'Prior'
                    ? `/prior-resident/${residentId}`
                    : `/resident/${residentId}`,
                ),
                value: headOfHousehold,
              }
            : {
                variant: 'string',
                value: headOfHousehold,
              },
        newGrossRent: {
          variant: 'currency',
          value: newGrossRent,
        },
        newHUDUA: {
          variant: 'currency',
          value: newHUDUA,
        },
        newSubsidy: getGrossRentCurrencyCell(newSubsidy, hasPriorCertData),
        newResidentRent: getGrossRentCurrencyCell(
          newResidentRent,
          hasPriorCertData && residentRentApplicable,
        ),
        newUtilityReimbursement: getGrossRentCurrencyCell(
          newUtilityReimbursement,
          hasPriorCertData && utilityReimbursementApplicable,
        ),
        grEffectiveDate: {
          variant: 'date',
          value: grEffectiveDate,
        },
        anticipatedVoucherDate: {
          variant: 'date',
          value: anticipatedVoucherDate,
        },
        priorCertificationDate: {
          variant: 'date',
          value: missingPreviousCertification ? null : priorCertificationDate,
        },
        otherCertInProgress: {
          variant: 'string',
          value: otherCertInProgress,
        },
        occupiedOnNTV: {
          variant: 'string',
          value: occupiedOnNTV,
        },
        residentSignatureRequired: {
          variant: 'string',
          value: missingPreviousCertification
            ? null
            : residentSignatureRequired,
        },
        generate59a: {
          variant: 'iconButton',
          iconName: 'DownloadIcon',
          color: 'primary',
          loading: generate59AInFlightIds.includes(affordableQualificationId),
          disabled: disableGenerate59A,
          onClick: () => download59A(affordableQualificationId),
        },
      };

      return row;
    });
  }, [
    canCancelCertifications,
    certifications,
    download59A,
    generate59AInFlightIds,
    handleOpenModal,
    openCancelModal,
  ]);
};
